import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type ClassroomDisciplineListItemViewModel,
  getApiV1ClassroomdisciplineQueryOptions,
} from '@/shared/api/generated';

import { classPlanningFiltersAtom } from '../store/atoms/classPlanningFiltersAtom';
import { buildClassPlanningCsv } from '../utils/buildClassPlanningCsv';
import { CLASS_PLANNING_STATUS_LABEL_KEY } from '../utils/classPlanningStatus';

/** Tamanho de página usado no export — maior que o da listagem para reduzir o número de requisições. */
const EXPORT_PAGE_SIZE = 100;

/** Trava de segurança contra loops indevidos caso o backend nunca sinalize o fim da paginação. */
const MAX_EXPORT_PAGES = 500;

/** Mesmo envelope paginado tolerado por `useClassPlanningList` (ver esse arquivo para detalhes). */
interface ClassPlanningEnvelope {
  readonly data?: ClassroomDisciplineListItemViewModel[] | null;
  readonly totalPages?: number;
}

/**
 * Hook de exportação da listagem de planejamento de aulas para CSV (Excel pt-BR).
 *
 * Busca **todas as páginas** do filtro atualmente aplicado (não só a página
 * visível), usando o mesmo endpoint da listagem com uma `PageSize` maior, monta o
 * CSV via `buildClassPlanningCsv` e dispara o download no navegador.
 *
 * @returns `{ exportCsv, isExporting }`.
 *
 * @example
 * ```tsx
 * const { exportCsv, isExporting } = useExportClassPlanningCsv();
 * <LinkButton disabled={isExporting} onClick={exportCsv}>Exportar Excel</LinkButton>
 * ```
 */
export function useExportClassPlanningCsv() {
  const { t } = useTranslation('classPlanning');
  const queryClient = useQueryClient();
  const filters = useAtomValue(classPlanningFiltersAtom);
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const params = {
        ...(filters.companyId !== null ? { CompanyId: filters.companyId } : {}),
        ...(filters.courseId !== null ? { CourseId: filters.courseId } : {}),
        ...(filters.classStudentId !== null ? { ClassStudentId: filters.classStudentId } : {}),
        ...(filters.classModuleId !== null ? { ClassModuleId: filters.classModuleId } : {}),
        ...(filters.status.length > 0 ? { Status: [...filters.status] } : {}),
        ...(filters.dateStart ? { DateStart: filters.dateStart } : {}),
        ...(filters.dateEnd ? { DateEnd: filters.dateEnd } : {}),
        ...(filters.teacherName ? { Teacher: filters.teacherName } : {}),
      };

      const rows: ClassroomDisciplineListItemViewModel[] = [];
      for (let page = 1; page <= MAX_EXPORT_PAGES; page += 1) {
        const raw = (await queryClient.fetchQuery(
          getApiV1ClassroomdisciplineQueryOptions({
            ...params,
            PageNumber: page,
            PageSize: EXPORT_PAGE_SIZE,
          }),
        )) as ClassroomDisciplineListItemViewModel[] | ClassPlanningEnvelope;
        const pageRows = Array.isArray(raw) ? raw : (raw?.data ?? []);
        rows.push(...pageRows);

        const envelope = Array.isArray(raw) ? undefined : raw;
        const hasMore =
          envelope?.totalPages !== undefined
            ? page < envelope.totalPages
            : pageRows.length === EXPORT_PAGE_SIZE;
        if (!hasMore || pageRows.length === 0) break;
      }

      const csv = buildClassPlanningCsv(
        rows,
        {
          module: t('table.columns.module'),
          date: t('table.columns.date'),
          discipline: t('table.columns.discipline'),
          classStudent: t('table.columns.classStudent'),
          teacher: t('table.columns.teacher'),
          status: t('table.columns.status'),
        },
        status => t(CLASS_PLANNING_STATUS_LABEL_KEY[status] as 'status.planned'),
      );

      // BOM (﻿) garante que o Excel reconheça UTF-8 e acentos corretamente.
      const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = t('list.exportFileName');
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCsv, isExporting } as const;
}
